import { useEffect, useState } from 'react';
import { Building2, CreditCard, Users, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { listPayload, objectPayload } from '@/lib/payload';
import { formatCurrencyAmount } from '@/lib/formatMoney';
import { completeMonths } from '@/lib/reportPeriods';
import { sortCategoricalRows } from '@/lib/chartPalette';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { MonthlyBarChart } from '@/components/charts/MonthlyBarChart';
import { StatusPieChart } from '@/components/charts/StatusPieChart';

type MonthCount = { month: string; count: number };
type ConversionRow = { month: string; invited: number; promo: number; organic: number };
type Summary = {
  totalUsers: number;
  totalGroups: number;
  activeSubscriptions: number;
  outstandingReceivables: Array<{ currency: string; total: number }>;
};

export function DashboardPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [users, setUsers] = useState<MonthCount[]>([]);
  const [groups, setGroups] = useState<MonthCount[]>([]);
  const [conversion, setConversion] = useState<ConversionRow[]>([]);
  const [gender, setGender] = useState<Array<{ gender: string; count: number }>>([]);
  const [age, setAge] = useState<Array<{ bucket: string; count: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // One toast id for the whole page: six report calls fail together whenever
    // the session or the network is the cause, and six identical toasts is
    // worse than one. sonner replaces a toast that reuses an existing id.
    const reportFailed = (error: unknown) =>
      toast.error(getApiErrorMessage(error), { id: 'dashboard-report-error' });

    void api
      .get('/reports/summary')
      .then((res) => setSummary(objectPayload(res)))
      .catch((error) => {
        setSummary(null);
        reportFailed(error);
      })
      .finally(() => setIsLoading(false));
    void api.get('/reports/growth/users-by-month?months=12').then((res) => setUsers(listPayload(res))).catch((error) => { setUsers([]); reportFailed(error); });
    void api.get('/reports/growth/groups-by-month?months=12').then((res) => setGroups(listPayload(res))).catch((error) => { setGroups([]); reportFailed(error); });
    void api.get('/reports/growth/conversion-summary?months=12').then((res) => setConversion(listPayload(res))).catch((error) => { setConversion([]); reportFailed(error); });
    void api.get('/reports/demographics/gender').then((res) => setGender(listPayload(res))).catch((error) => { setGender([]); reportFailed(error); });
    void api.get('/reports/demographics/age-distribution').then((res) => setAge(listPayload(res))).catch((error) => { setAge([]); reportFailed(error); });
  }, []);

  const runningTotal = (rows: MonthCount[]) =>
    rows.reduce<MonthCount[]>((acc, row) => {
      const previous = acc.length > 0 ? acc[acc.length - 1].count : 0;
      acc.push({ month: row.month, count: previous + row.count });
      return acc;
    }, []);

  /**
   * Every currency is shown, none is promoted to the headline. The API orders
   * outstandingReceivables by raw total descending, which compares unconverted
   * amounts across currencies: UGX 1,000,000 outranks USD 5,000 despite being
   * a far smaller exposure. Picking [0] as "the" figure would bake that
   * meaningless comparison into the landing page, so the card reports how many
   * currencies are outstanding and lists each amount beneath.
   */
  const outstanding = summary?.outstandingReceivables ?? [];
  const outstandingValue =
    outstanding.length === 0
      ? 'None'
      : outstanding.length === 1
        ? formatCurrencyAmount(outstanding[0].total, outstanding[0].currency)
        : `${outstanding.length} currencies`;
  const outstandingHint =
    outstanding.length > 1
      ? outstanding.map((row) => formatCurrencyAmount(row.total, row.currency)).join(', ')
      : undefined;

  interface MonthOverMonth {
    change: string;
    changeType: 'positive' | 'negative' | 'neutral';
  }

  /**
   * Month-over-month change between the last two COMPLETE months of a monthly
   * count series (the caller strips the current, partial month first). Returns
   * null when there is no prior complete month or that month was zero, so the
   * caller omits the chip rather than rendering a meaningless percentage.
   *
   * `noun` names what actually moved. These series count NEW users/groups per
   * month, while the card shows the all-time cumulative total, so an unlabelled
   * percentage would read as growth in the total. It is not: it is growth in
   * the signup rate.
   */
  const monthOverMonth = (rows: MonthCount[], noun: string): MonthOverMonth | null => {
    if (rows.length < 2) return null;
    const previous = rows[rows.length - 2].count;
    const latest = rows[rows.length - 1].count;
    if (previous === 0) return null;
    const pct = ((latest - previous) / previous) * 100;
    const changeType = pct === 0 ? 'neutral' : pct > 0 ? 'positive' : 'negative';
    return { change: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% ${noun} MoM`, changeType };
  };

  const usersChange = monthOverMonth(completeMonths(users), 'new signups');
  const groupsChange = monthOverMonth(completeMonths(groups), 'new groups');

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold">Platform overview</h1>
        <p className="text-muted-foreground">Growth, adoption and demographics across all groups.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total users"
          value={summary ? summary.totalUsers.toLocaleString() : '0'}
          icon={Users}
          iconColor="primary"
          isLoading={isLoading}
          {...(usersChange ?? {})}
          className="animate-fade-up"
        />
        <StatCard
          title="Total groups"
          value={summary ? summary.totalGroups.toLocaleString() : '0'}
          icon={Building2}
          iconColor="secondary"
          isLoading={isLoading}
          {...(groupsChange ?? {})}
          className="animate-fade-up [animation-delay:100ms]"
        />
        <StatCard
          title="Active subscriptions"
          value={summary ? summary.activeSubscriptions.toLocaleString() : '0'}
          icon={CreditCard}
          iconColor="accent"
          isLoading={isLoading}
          className="animate-fade-up [animation-delay:200ms]"
        />
        <StatCard
          title="Outstanding receivables"
          value={outstandingValue}
          hint={outstandingHint}
          icon={Wallet}
          iconColor="accent"
          isLoading={isLoading}
          className="animate-fade-up [animation-delay:300ms]"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Users by month</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart data={users} series={[{ key: 'count', label: 'New users' }]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Groups by month</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart data={groups} series={[{ key: 'count', label: 'New groups' }]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Cumulative users</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart data={runningTotal(users)} series={[{ key: 'count', label: 'Total users' }]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Cumulative groups</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart data={runningTotal(groups)} series={[{ key: 'count', label: 'Total groups' }]} />
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Conversion breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart
              data={conversion}
              series={[
                { key: 'invited', label: 'Invited' },
                { key: 'promo', label: 'Promo' },
                { key: 'organic', label: 'Organic' },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Gender</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusPieChart data={sortCategoricalRows(gender, 'gender')} nameKey="gender" valueKey="count" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Age distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart
              data={age.map((row) => ({ month: row.bucket, count: row.count }))}
              series={[{ key: 'count', label: 'Users' }]}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
